import { ChangeDetectionStrategy, Component, ElementRef, computed, input, model, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { ButtonDirective } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { ChapterData, ProjectData } from '../../models';

export type CopyTargetKind = 'scene' | 'image';

export interface CopyTargetSelection {
	kind: CopyTargetKind;
	chapterSlug: string | null;
	slug: string;
}

interface TargetGroup {
	label: string;
	chapterSlug: string | null;
	scenes: { slug: string; name?: string }[];
	images: { slug: string; name?: string }[];
}

@Component({
	selector: 'app-copy-target-dialog',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [Dialog, FormsModule, ButtonDirective, InputText],
	templateUrl: './copy-target-dialog.html',
	styleUrl: './copy-target-dialog.scss',
})
export class CopyTargetDialogComponent {
	visible = model(false);
	project = input<ProjectData | undefined>(undefined);
	sourceLabel = input<string>('');
	header = input<string>('Copy content item');
	targetKinds = input<CopyTargetKind[]>(['scene', 'image']);
	confirmLabel = input<string>('Copy here');

	targetSelected = output<CopyTargetSelection>();

	protected searchQuery = signal('');
	protected selected = signal<CopyTargetSelection | null>(null);
	private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

	protected showScenes = computed(() => this.targetKinds().includes('scene'));
	protected showImages = computed(() => this.targetKinds().includes('image'));

	protected searchPlaceholder = computed(() => {
		if (!this.showImages()) return 'Search scenes...';
		if (!this.showScenes()) return 'Search images...';
		return 'Search scenes / images...';
	});

	protected groups = computed<TargetGroup[]>(() => {
		const project = this.project();
		if (!project) return [];
		const out: TargetGroup[] = [];

		for (const chapter of (project.chapters ?? []) as ChapterData[]) {
			out.push({
				label: chapter.name?.trim() || chapter.slug,
				chapterSlug: chapter.slug,
				scenes: (chapter.scenes ?? []).map((s) => ({ slug: s.slug })),
				images: (chapter.images ?? []).map((i) => ({ slug: i.slug })),
			});
		}

		const projectScenes = project.scenes ?? [];
		if (projectScenes.length > 0) {
			out.push({
				label: 'Project scenes',
				chapterSlug: null,
				scenes: projectScenes.map((s) => ({ slug: s.slug })),
				images: [],
			});
		}

		const projectImages = project.images ?? [];
		if (projectImages.length > 0) {
			out.push({
				label: 'Project images',
				chapterSlug: null,
				scenes: [],
				images: projectImages.map((i) => ({ slug: i.slug })),
			});
		}

		return out;
	});

	protected filteredGroups = computed<TargetGroup[]>(() => {
		const query = this.searchQuery().toLowerCase().trim();
		const showScenes = this.showScenes();
		const showImages = this.showImages();
		return this.groups()
			.map((g) => ({
				...g,
				scenes: showScenes ? g.scenes.filter((s) => !query || s.slug.toLowerCase().includes(query)) : [],
				images: showImages ? g.images.filter((i) => !query || i.slug.toLowerCase().includes(query)) : [],
			}))
			.filter((g) => g.scenes.length > 0 || g.images.length > 0);
	});

	protected isSelected(kind: CopyTargetKind, chapterSlug: string | null, slug: string): boolean {
		const sel = this.selected();
		return !!sel && sel.kind === kind && sel.chapterSlug === chapterSlug && sel.slug === slug;
	}

	protected selectTarget(kind: CopyTargetKind, chapterSlug: string | null, slug: string): void {
		this.selected.set({ kind, chapterSlug, slug });
	}

	protected confirm(): void {
		const sel = this.selected();
		if (!sel) return;
		this.targetSelected.emit(sel);
		this.visible.set(false);
	}

	protected cancel(): void {
		this.visible.set(false);
	}

	protected onHide(): void {
		this.searchQuery.set('');
		this.selected.set(null);
	}

	/**
	 * Explicit autofocus for the search input. PrimeNG's Dialog already focuses
	 * the first focusable child today, so this is defensive — keeps the focus
	 * landing predictable if a future template change inserts another focusable
	 * element ahead of the search input.
	 */
	protected onShow(): void {
		this.searchInput()?.nativeElement.focus();
	}
}
